import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  EnvelopeIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchMspNotificationSettings,
  updateMspNotificationSetting,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

export default function MspNotificationSettingsManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-MSP local edit state, keyed by mspCode: { recipients, enabled }.
  const [edits, setEdits] = useState({});
  const [savingCode, setSavingCode] = useState(null);
  const [rowError, setRowError] = useState({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMspNotificationSettings();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const seed = {};
      list.forEach((r) => { seed[r.mspCode] = { recipients: r.emailRecipients || '', enabled: !!r.emailEnabled }; });
      setEdits(seed);
    } catch (err) {
      setError(err.message || 'Failed to load MSP notification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isDirty = useCallback((r) => {
    const e = edits[r.mspCode];
    if (!e) return false;
    return e.recipients !== (r.emailRecipients || '') || e.enabled !== !!r.emailEnabled;
  }, [edits]);

  const setEdit = (code, patch) => {
    setEdits((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  };

  const handleSave = async (r) => {
    const e = edits[r.mspCode];
    if (!e) return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setSavingCode(r.mspCode);
    try {
      const updated = await updateMspNotificationSetting(r.mspCode, {
        emailRecipients: e.recipients,
        emailEnabled: e.enabled,
      });
      setRows((prev) => prev.map((item) => (item.mspCode === r.mspCode ? { ...item, ...updated } : item)));
      setEdit(r.mspCode, { recipients: updated.emailRecipients || '', enabled: !!updated.emailEnabled });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: err.message || 'Failed to save' }));
    } finally {
      setSavingCode(null);
    }
  };

  const enabledCount = useMemo(() => rows.filter((r) => !!r.emailEnabled).length, [rows]);

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <EnvelopeIcon className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-100">MSP Alert Email Routing</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-MSP email recipients for detection alerts. Each MSP receives alert emails for the M365 tenants it
          manages. Toggling an MSP's email path on or off is independent of the global Teams channel notification —
          turning email off here never affects Teams. Recipients are comma-separated.
        </p>
      </div>

      {!isAdmin && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to change email routing.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      <div className="flex items-center justify-end mb-3">
        <span className="text-xs text-gray-500">
          {rows.length} MSP{rows.length === 1 ? '' : 's'} · {enabledCount} with email enabled
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-gray-800">
                {['MSP', 'Email Recipients (comma-separated)', 'Email Enabled', 'Last Updated', ''].map((label) => (
                  <th key={label} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
                      <span className="text-gray-500 text-sm">Loading MSP notification settings...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <EnvelopeIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No MSPs configured</p>
                    <p className="text-gray-600 text-sm mt-1">Set the MSP_CATALOG app setting to populate this list.</p>
                  </td>
                </tr>
              )}

              {!loading && rows.map((r) => {
                const e = edits[r.mspCode] || { recipients: '', enabled: false };
                const dirty = isDirty(r);
                const saving = savingCode === r.mspCode;
                return (
                  <tr key={r.mspCode} className="hover:bg-gray-800/50 transition-colors align-top">
                    <td className="px-4 py-3">
                      <div className="font-mono text-gray-200">{r.mspCode}</div>
                      {r.displayName && <div className="text-xs text-gray-500">{r.displayName}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={e.recipients}
                        disabled={!isAdmin}
                        onChange={(ev) => setEdit(r.mspCode, { recipients: ev.target.value })}
                        placeholder="soc@msp.com, alerts@msp.com"
                        className="input-field w-full text-xs disabled:opacity-60"
                      />
                      {rowError[r.mspCode] && <p className="mt-1 text-xs text-red-400">{rowError[r.mspCode]}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={e.enabled}
                          disabled={!isAdmin}
                          onChange={(ev) => setEdit(r.mspCode, { enabled: ev.target.checked })}
                          className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                        />
                        <span className={`text-xs ${e.enabled ? 'text-green-400' : 'text-gray-500'}`}>
                          {e.enabled ? 'Enabled' : '(currently disabled)'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(r.updatedUtc)}
                      {r.updatedBy && <div className="text-gray-600 truncate max-w-[180px]" title={r.updatedBy}>{r.updatedBy}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && (
                        <button
                          onClick={() => handleSave(r)}
                          disabled={!dirty || saving}
                          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                        >
                          {saving ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckIcon className="h-3.5 w-3.5" />}
                          Save
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
